<!-- Project thumbnail — shared by both variants; the caller sizes the frame. -->
<ng-template #projectThumb let-project="project" let-frameClass="frameClass">
  <div class="relative shrink-0 overflow-hidden {{ frameClass }}">
    @if ((project?.gallery || [])?.[0]?.image || project?.['gallery_first_image']?.[0]?.image) {
      @defer (on viewport) {
        <img
          [ngSrc]="
            apiService.IMAGE_BASE_URL +
            ((project?.gallery || [])?.[0]?.image || project?.['gallery_first_image']?.[0]?.image) +
            '&variant=thumbnail'
          "
          [alt]="project?.['name'] || 'Project image'"
          class="object-cover"
          fill
          loading="lazy"
        />
      } @placeholder {
        <div class="absolute inset-0 bg-[rgb(239_239_239)] border border-black/10"></div>
      } @loading {
        <div class="absolute inset-0 bg-[rgb(239_239_239)] border border-black/10 overflow-hidden">
          <div
            class="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-black/10 to-transparent animate-[shimmer_1.5s_infinite]"
          ></div>
        </div>
      }
    } @else {
      <div class="absolute inset-0 bg-[rgb(239_239_239)] border border-black/10"></div>
    }
  </div>
</ng-template>

<div [class]="wrapperClass()">
  <div class="flex flex-row items-center gap-2">
    <div class="relative grow">
      <app-text-input
        [ngModel]="searchTerm()"
        (ngModelChange)="searchTerm.set($event)"
        placeholder="Search projects"
        [className]="searchInputClass()"
      ></app-text-input>
      <img src="../../../assets/images/svg/search.svg" [class]="searchIconClass()" alt="Search" />
    </div>
    <!-- The header's global "+" is desktop-only now, so the responsive menu
         carries the Add Project shortcut itself (FEC-1150 QA). -->
    @if (showAddProject()) {
      <a
        routerLink="/project/add"
        aria-label="Add project"
        title="Add project"
        (click)="itemSelected.emit()"
        class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-dark text-white cursor-pointer transition-fec hover:bg-dark-grey dark:bg-white dark:text-dark dark:hover:bg-grey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
      >
        <app-icon name="plus" [size]="20" />
      </a>
    }
  </div>
  <div class="flex flex-col justify-start items-stretch gap-2">
    @if (variant() === 'inline') {
      <!-- ═══ Responsive menu (FEC-1150): collapsible precinct groups ═══
           No inner scroll container here — a scroll inside the menu's own
           scroll reads badly on a phone, so the sheet scrolls as one list and
           a single precinct stays open at a time (FEC-1150 QA). -->
      <ul class="list-none flex flex-col justify-start items-stretch gap-1">
        <li
          routerLink="/"
          (click)="itemSelected.emit()"
          (keydown.enter)="itemSelected.emit()"
          (keydown.space)="itemSelected.emit()"
          role="button"
          tabindex="0"
          class="flex justify-start items-center gap-[7px] py-2 cursor-pointer select-none rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
        >
          <img src="../../../assets/images/svg/project-agent-access-logo.svg" alt="" />
          <p class="text-sm">All Projects</p>
        </li>
        @for (precinct of filteredPrecinctListing(); track $index) {
          <li class="flex flex-col justify-start items-stretch gap-1">
            <button
              type="button"
              class="flex flex-row justify-between items-center gap-2 p-2 rounded-lg bg-muted dark:bg-surface-dark-muted select-none cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
              [attr.aria-expanded]="isPrecinctExpanded(precinct?.['name'])"
              (click)="togglePrecinct(precinct?.['name'])"
            >
              <span class="text-sm">{{ precinct?.['name'] }}</span>
              <app-icon
                name="chevron"
                [size]="16"
                class="transition-transform"
                [class.rotate-180]="isPrecinctExpanded(precinct?.['name'])"
              />
            </button>
            @if (isPrecinctExpanded(precinct?.['name'])) {
              <ul class="list-none flex flex-col justify-start items-stretch">
                @for (project of precinct?.['projects'] || []; track $index) {
                  <li
                    [routerLink]="['/project-detail', project?.['id'], 'dashboard']"
                    (click)="itemSelected.emit()"
                    (keydown.enter)="itemSelected.emit()"
                    (keydown.space)="itemSelected.emit()"
                    role="button"
                    tabindex="0"
                    class="flex justify-start items-center gap-[7px] p-2 rounded-xl cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
                  >
                    <ng-container
                      *ngTemplateOutlet="
                        projectThumb;
                        context: { project: project, frameClass: 'w-10 h-10 rounded-[9px]' }
                      "
                    ></ng-container>
                    <p class="text-sm">{{ project?.['name'] }}</p>
                  </li>
                }
              </ul>
            }
          </li>
        }
      </ul>
    } @else {
      <ul
        class="list-none flex flex-col justify-start items-stretch gap-2 overflow-y-auto max-h-96 no-scrollbar-dropdown"
      >
        <li
          routerLink="/"
          (click)="itemSelected.emit()"
          (keydown.enter)="itemSelected.emit()"
          (keydown.space)="itemSelected.emit()"
          role="button"
          tabindex="0"
          class="flex justify-start items-center gap-2 cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
        >
          <img src="../../../assets/images/svg/project-agent-access-logo.svg" alt="" />
          <p class="text-base">All Projects</p>
        </li>
        @for (precinct of filteredPrecinctListing(); let precinctIndex = $index; track $index) {
          <li
            class="h-8 flex flex-row justify-start items-center gap-[10px] p-2 rounded-lg bg-muted dark:bg-surface-dark-muted select-none"
          >
            <p class="text-xs">{{ precinct?.['name'] }}</p>
          </li>
          @for (project of precinct?.['projects'] || []; track $index) {
            <li
              [routerLink]="['/project-detail', project?.['id'], 'dashboard']"
              (click)="itemSelected.emit()"
              (keydown.enter)="itemSelected.emit()"
              (keydown.space)="itemSelected.emit()"
              role="button"
              tabindex="0"
              class="flex justify-start items-center gap-2 cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
            >
              <ng-container
                *ngTemplateOutlet="
                  projectThumb;
                  context: { project: project, frameClass: 'w-[42px] min-h-[42px] rounded-xl' }
                "
              ></ng-container>
              <p class="text-base">{{ project?.['name'] }}</p>
            </li>
          }
        }
      </ul>
    }
  </div>
  <div
    class="flex flex-row justify-between items-center gap-2 pt-3 mt-1 border-t border-black/5 dark:border-white/10"
  >
    <label
      [for]="toggleId()"
      class="text-sm select-none cursor-pointer text-dark hover:text-dark-grey dark:text-white dark:hover:text-grey transition-fec"
    >
      {{ toggleLabel() }}
    </label>
    <app-switch-input
      [inputId]="toggleId()"
      [ariaLabel]="toggleLabel()"
      [ngModel]="showAll()"
      (ngModelChange)="showAll.set($event)"
    />
  </div>
</div>
